import { useDispatch, useSelector } from 'hooks/reactRedux';

import { setUserPreference } from 'redux/actionCreators';

import { HOURS_GROUPS } from '../columns/columnSpec';
import { DETAIL_LAYOUT_PREFERENCE_KEY } from '../EmploymentHours.consts';

import type { HoursGroupId } from '../columns/columnSpec.types';

export type HoursDetailLayout = {
  /** group ids in the order the user dragged them; groups it omits keep their spec order after it */
  order: HoursGroupId[];
  /** the tables the user removed from the detail panel */
  removed: HoursGroupId[];
};

const EMPTY_LAYOUT: HoursDetailLayout = { order: [], removed: [] };

const isGroupId = (value: unknown): value is HoursGroupId =>
  HOURS_GROUPS.some((group) => group.id === value);

const safeParse = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/**
 * Keeps only ids that still exist. The group set is generated from the spec, so a preference saved
 * before a group was renamed or dropped would otherwise pin a table that can never render.
 */
const sanitise = (stored: unknown): HoursDetailLayout => {
  const parsed = typeof stored === 'string' ? safeParse(stored) : stored;

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return EMPTY_LAYOUT;
  }

  const { order, removed } = parsed as Partial<HoursDetailLayout>;

  return {
    order: (Array.isArray(order) ? order : []).filter(isGroupId),
    removed: (Array.isArray(removed) ? removed : []).filter(isGroupId)
  };
};

/** Spec order for anything the stored order does not mention, so new groups simply appear. */
const sortByLayout = (groupIds: HoursGroupId[], order: HoursGroupId[]) => {
  const rank = (groupId: HoursGroupId) => {
    const index = order.indexOf(groupId);

    return index === -1 ? order.length + groupIds.indexOf(groupId) : index;
  };

  return [...groupIds].sort((left, right) => rank(left) - rank(right));
};

/**
 * Detail-panel layout — table order and removed tables — persisted per user server-side through the
 * same preferences saga as the column picker (`useHoursColumnVisibility`).
 */
export const useHoursDetailLayout = () => {
  'use memo';

  const dispatch = useDispatch();
  const stored = useSelector(
    (state) => state.preferences?.[DETAIL_LAYOUT_PREFERENCE_KEY]
  );

  const layout = sanitise(stored);
  const removed = new Set(layout.removed);

  const save = (next: HoursDetailLayout) => {
    dispatch(
      setUserPreference({ key: DETAIL_LAYOUT_PREFERENCE_KEY, value: next })
    );
  };

  return {
    /** the tables of one section, in the user's order, removed ones included */
    orderedGroups: (groupIds: HoursGroupId[]) =>
      sortByLayout(groupIds, layout.order),

    /** the visible tables of one section, in the user's order */
    visibleGroups: (groupIds: HoursGroupId[]) =>
      sortByLayout(groupIds, layout.order).filter(
        (groupId) => !removed.has(groupId)
      ),

    /**
     * Reorders within one section: only the dragged section's ids move, and the stored order keeps
     * every other section's ids where they were.
     */
    moveGroup: (
      sectionGroupIds: HoursGroupId[],
      activeId: HoursGroupId,
      overId: HoursGroupId
    ) => {
      const sectionOrder = sortByLayout(sectionGroupIds, layout.order);
      const from = sectionOrder.indexOf(activeId);
      const to = sectionOrder.indexOf(overId);

      if (from === -1 || to === -1 || from === to) {
        return;
      }

      sectionOrder.splice(to, 0, ...sectionOrder.splice(from, 1));

      const others = layout.order.filter(
        (groupId) => !sectionGroupIds.includes(groupId)
      );

      save({ ...layout, order: [...others, ...sectionOrder] });
    },

    isRemoved: (groupId: HoursGroupId) => removed.has(groupId),

    /** the picker switches and the tables' own remove buttons both land here */
    toggleGroup: (groupId: HoursGroupId) =>
      save({
        ...layout,
        removed: removed.has(groupId)
          ? layout.removed.filter((candidate) => candidate !== groupId)
          : [...layout.removed, groupId]
      })
  };
};

export const hoursDetailLayoutInternals = { sanitise, sortByLayout };
